import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type Control, type UseFormSetValue } from 'react-hook-form';

import type { ReceiveConfirmContextDto } from '@/entities/receive';
import { DataTable } from '@/shared/ui/data-table';

import { supportsHandlingUnits } from '../lib/quantities';
import { buildConfirmArrivalTableRows, type ConfirmArrivalTableRow } from '../lib/table-rows';
import { CONFIRM_ARRIVAL_UNIT_MODE } from '../model/constants';
import { useSecondaryUnitCells } from '../model/use-secondary-unit-cells';
import type { ConfirmArrivalFormValues, ConfirmArrivalQuantityField, ConfirmArrivalUnitMode } from '../model/types';
import { getConfirmArrivalColumns } from './confirm-arrival-columns';
import { ConfirmArrivalHandlingSummary } from './confirm-arrival-handling-summary';
import { ConfirmArrivalUnitToolbar } from './confirm-arrival-unit-toolbar';

interface ConfirmArrivalLinesTableProps {
  control: Control<ConfirmArrivalFormValues>;
  setValue: UseFormSetValue<ConfirmArrivalFormValues>;
  context: ReceiveConfirmContextDto;
  unitMode: ConfirmArrivalUnitMode;
  onUnitModeChange: (mode: ConfirmArrivalUnitMode) => void;
  onResetQuantities: () => void;
}

const isGroupRow = (row: ConfirmArrivalTableRow) => row.kind === 'consignment';

const renderGroupRow = (row: ConfirmArrivalTableRow) =>
  row.kind === 'consignment' ? (
    <span className="text-sm font-medium">
      {row.consignmentNumber}
      {row.organization && <span className="ml-2 font-normal text-muted-foreground">{row.organization}</span>}
    </span>
  ) : null;

/** The consignment lines in the shared `DataTable`; a waybill receipt heads each consignment with a group row. */
export function ConfirmArrivalLinesTable({
  control,
  setValue,
  context,
  unitMode,
  onUnitModeChange,
  onResetQuantities
}: ConfirmArrivalLinesTableProps) {
  const { t } = useTranslation();

  const anyBundleLines = context.lines.some(supportsHandlingUnits);
  const { isSecondaryOpen, toggleSecondary, anySecondaryOpen } = useSecondaryUnitCells();
  // A second input always shows the unit the default does not, so with an
  // inventory default any open one is a handling figure — an estimate too.
  const showsHandlingFigures = unitMode === CONFIRM_ARRIVAL_UNIT_MODE.HANDLING || anySecondaryOpen;

  const rows = useMemo(() => buildConfirmArrivalTableRows(context), [context]);

  const columns = useMemo(() => {
    const fieldLabels: Record<ConfirmArrivalQuantityField, string> = {
      goodQty: t('receive.goodquantity.label', 'Received in Good Condition'),
      damagedQty: t('receive.badquantity.label', 'Received in Damaged Condition'),
      lostQty: t('receive.lostintransit.label', 'Lost in Transit')
    };
    return getConfirmArrivalColumns({
      control,
      setValue,
      unitMode,
      showLostColumn: context.lostQtyAllowed,
      fieldLabels,
      lineLabel: t('receive.requestline.label', 'Consignment Line'),
      expectedLabel: t('receive.expectedquantity.label', 'Quantity Expected'),
      remainingLabel: t('receive.remainingquantity.label', 'Remaining Quantity'),
      isSecondaryOpen,
      onToggleSecondary: toggleSecondary
    });
  }, [t, control, setValue, unitMode, context.lostQtyAllowed, isSecondaryOpen, toggleSecondary]);

  return (
    <div className="space-y-3">
      <ConfirmArrivalUnitToolbar
        showUnitSwitch={anyBundleLines}
        unitMode={unitMode}
        onUnitModeChange={onUnitModeChange}
        onResetQuantities={onResetQuantities}
        showsHandlingFigures={showsHandlingFigures}
      />

      <ConfirmArrivalHandlingSummary control={control} lines={context.lines} />

      <DataTable
        data={rows}
        columns={columns}
        tableId="receive-confirm-arrival-lines"
        showToolbar={false}
        enableColumnCustomization={false}
        enableFullscreen={false}
        height="h-auto"
        contentClassName="px-0"
        isGroupRow={isGroupRow}
        renderGroupRow={renderGroupRow}
      />
    </div>
  );
}
